/**
 * UnitStatus — Tier 0 slice-local primitive (SCRUM-167 epic).
 *
 * Renders the canonical Active·Bookable / Active·Non-bookable /
 * Inactive / Invalid badge tuple per
 * `~/Desktop/design_handoff_properties/STATUS_MATRIX.md` §6. Wraps
 * `@sparta/ui-kit`'s `<Badge>` with the variant + icon + text
 * combinations the matrix specifies — color is never the only
 * conveyor (icon + text reinforce the state for a11y).
 *
 * The "Invalid" branch (`op === 'inactive' && av === 'bookable'`) is
 * intentionally renderable. The hard guard lives in `UnitStatusModal`
 * (Bookable radio is `disabled` when Inactive selected, Save disabled
 * in forbidden state, BE returns 422). This primitive is purely
 * presentational — it cannot enforce; the modal does. The Invalid
 * branch exists as a safety net in case bad data ever reaches the
 * list view.
 *
 * Used in 4 places across the Properties slice (list units view ·
 * Units tab · Unit detail hero · Status modal preview). Second-
 * consumer trigger fires the kit-promotion ladder if a non-Properties
 * slice (Reservations, etc.) wants the same badge.
 *
 * Field naming follows the bundle (`op` / `av` short forms). The DTO
 * mapper translates the BE's `billing` / `visibility` → `op` / `av`
 * + normalises `non_bookable` (underscore, BE) → `non-bookable`
 * (hyphen, bundle).
 */

import * as React from 'react';
import { Badge, type BadgeSize } from '@sparta/ui-kit';

export type UnitOperational = 'active' | 'inactive';
export type UnitAvailability = 'bookable' | 'non-bookable';

export interface UnitStatusProps {
  readonly op: UnitOperational;
  readonly av: UnitAvailability;
  readonly size?: BadgeSize;
}

/**
 * The four states from STATUS_MATRIX.md §6 — exhaustive over the
 * cartesian product of `(op, av)`. Returning `null` for unknown
 * combinations is not an option (`UnitOperational × UnitAvailability`
 * has only four members), so we use a switch chain that the
 * typescript exhaustiveness check would catch on extension.
 */
export function UnitStatus({
  op,
  av,
  size = 'md',
}: UnitStatusProps): React.ReactElement {
  if (op === 'active' && av === 'bookable') {
    return (
      <Badge variant="success" icon="check" size={size}>
        Active · Bookable
      </Badge>
    );
  }
  if (op === 'active' && av === 'non-bookable') {
    return (
      <Badge variant="warning" icon="bolt" size={size}>
        Active · Non-bookable
      </Badge>
    );
  }
  if (op === 'inactive' && av === 'non-bookable') {
    return (
      <Badge variant="neutral" icon="minus" size={size}>
        Inactive
      </Badge>
    );
  }
  // op === 'inactive' && av === 'bookable' — the forbidden combo.
  // STATUS_MATRIX.md §3: "purely a safety net; should never reach
  // production data". The Modal's hard guard + BE 422 keep it out.
  return (
    <Badge variant="danger" icon="bolt" size={size}>
      Invalid state
    </Badge>
  );
}
